import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { trigger, transition, style, animate,state } from '@angular/animations';
import { RecommendationService } from '../../../../core/services/recommendation.service';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Utils } from '../../../../shared/utils';
import { KebabComponent } from '../../../../layout/designs/kebab-component/kebab-component';
import { ViewChildren,QueryList,ViewChild,ElementRef } from '@angular/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { RouterModule } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID,inject } from '@angular/core';
import { Input,Output,EventEmitter } from '@angular/core';
import { TeaserTrailerService } from '../../../../core/services/trailers.service';
import { YoutubePlayerService } from '../../../../core/services/youtube.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef } from '@angular/core';
import { LanguageService } from '../../../../core/services/language/language.service';

@Component({
  selector: 'app-tv-recommendations',
  imports: [
    CommonModule,
    RouterModule,
    KebabComponent
  ],
  standalone : true,
  templateUrl: './tv-recommendations.html',
  styleUrl: './tv-recommendations.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeAndToggle', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      state('active', style({ transform: 'scale(1)', background: 'linear-gradient(340deg, #96f2d7, #96f2d7, #38d9a9, #12b886)',borderRadius : '2.5em'})),
      state('inactive', style({ transform: 'scale(1)', backgroundColor: '#f8f9fa', borderRadius : '2.5em' })),
      transition('inactive => active', [
        animate('200ms cubic-bezier(.68,-0.55,.27,1.55)')
      ]),
      transition('active => inactive', [
        animate('200ms ease-out')
      ])
    ])
  ]
})

export class TvRecommendations implements OnInit, AfterViewInit {

    @Input() showPlayer: boolean = false;
    @Output() showPlayerChange = new EventEmitter<boolean>();
    @ViewChild('trailerCardsContainer') trailerCardsContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('trailerContainer') trailerContainer!: ElementRef<HTMLDivElement>;
    @ViewChildren(KebabComponent) kebabs!: QueryList<KebabComponent>;

  constructor(
    private recommendationService: RecommendationService, 
    private route: ActivatedRoute,
    private utils:Utils,
    private youtubeService: YoutubePlayerService,
    private trailerService:TeaserTrailerService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef
  ) {}

  trailerId: string = '';
  tvId: number | null = null;
  tvName: string | null = null;
  recommendations: any[] = [];
  frontendUrl: string = environment.frontendUrl;
  backendUrl: string = environment.backendUrl;
  selectedItemIndex: number = 0;
  isActive: HTMLDivElement | null = null;
  kebabActive: boolean = true;
  activeCard : HTMLDivElement|null = null;
  activeCardIndex: number | null = null;
  readonly initialSkeletons = Array(4).fill(0);
  skeletonCount: number = 6;
  squareSkeletonTheme: any = {};
  lineSkeletonTheme: any = {};
  canScrollLeft: boolean = false;
  canScrollRight: boolean = true;
  currentTrailerKey: string = '';
  private platformId = inject(PLATFORM_ID);
  public readonly languageService = inject(LanguageService);
  private destroy$ = new Subject<void>();

  isLoading: boolean = true;

  ngOnInit(): void {
    this.updateSkeletonCount();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.fetchRecommendations(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.tvId) {
        this.fetchRecommendations();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchRecommendations(slugParam?: string | null): void {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');
    const [id, name] = slug ? slug?.split('-') : [null];
    this.tvId = id ? parseInt(id, 10) : null;
    this.tvName = name ? decodeURIComponent(name.replace(/-/g, ' ')) : null;

    if (this.tvId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.recommendationService.getRecommendations(this.tvId, 'tv', lang).subscribe({
        next: (recommendations) => {
          this.recommendations = recommendations?.results || [];
          this.isLoading = false;
          setTimeout(() => this.checkScroll(), 300);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.checkScroll(), 400);
    }
  }

  formattedMovieName(sentence: string): string {
    return this.utils.movieNameSplitter(sentence);
  }

  tvNameSplitter(word:string){
    return this.utils.movieNameSplitter(word);
  }

  getMediaRoute(movie: any): (string | number)[] {
    const type = movie?.media_type || (movie?.first_air_date || (movie?.name && !movie?.title) ? 'tv' : 'tv');
    const title = movie?.name || movie?.title || movie?.original_name || movie?.original_title || 'details';
    const slug = `${movie.id}-${this.utils.movieNameSplitter(title) || 'details'}`;
    return ['/', type, slug];
  }

  onImageError(event:Event){
    return this.utils.onImageVerticalError(event)
  }

  onKebabToggle(clickedKebab: KebabComponent,index:number) {
  
    this.kebabs.forEach(kebab => {
      if (kebab !== clickedKebab) kebab.isActive = false;
    });
    
    if (!clickedKebab.isActive) {
      this.activeCardIndex = null;
    } else {
      this.activeCardIndex = index;
    }
  }

   updateSkeletonCount() {
    if (!isPlatformBrowser(this.platformId)) return;
    const width = window.innerWidth;

    if (width < 768) {
      this.squareSkeletonTheme = {
        width: '85vw',
        height: '240px',
        borderRadius: '1rem',
        margin: '0 0.5rem'
      };
      this.skeletonCount = 3;
    } else if (width < 1024) {
      this.squareSkeletonTheme = {
        width: '360px',
        height: '203px',
        borderRadius: '1rem',
        margin: '0 0.75rem'
      };
      this.skeletonCount = 4;
    } else {
      this.squareSkeletonTheme = {
        width: '420px',
        height: '236px',
        borderRadius: '1rem',
        margin: '0 0.75rem'
      };
      this.skeletonCount = 3;
    }
  }

  onCardHover(index: number) {
    this.activeCardIndex = index;
    this.selectedItemIndex = index;
  }

  onCardLeave() {
    this.activeCardIndex = null;
  }

    async playTrailer(movie: any): Promise<void> {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const movieId = movie.id;
      const mediaType = movie.first_air_date ? 'tv' : 'movie';

      this.trailerService
        .getTeaserOrTrailers(parseInt(movieId), mediaType)
        .subscribe(trailers => {
          const trailer = trailers?.results?.find((t: any) =>
            t.type === 'Trailer' && (t.official === true)
          ) || trailers?.results?.find((t: any) => t.type === 'Trailer')
            || trailers?.results?.[0];

          if (trailer) {
            this.trailerId = trailer.key;
            
            // Emit to parent to show modal
            this.showPlayer = true;
            this.showPlayerChange.emit(true);
            
            // Prevent body scroll
            document.body.style.overflow = 'hidden';

            // Wait for modal to render, then find the element directly
            setTimeout(() => {
              const trailerElement = document.getElementById('trailer-player');
              
              if (trailerElement) {
                this.youtubeService.playTrailer(this.trailerId, 'trailer-player');
              }
            }, 400);
            
          } else {
            this.showPlayer = false;
            this.showPlayerChange.emit(false);
          }
        });
    }


  closeTrailer(): void {
    this.showPlayer = false;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
    
    if (this.youtubeService) {
      this.youtubeService.destroyPlayer();
    }
  }

  private updateScrollState() {
    if (!isPlatformBrowser(this.platformId)) return;
    requestAnimationFrame(() => this.checkScroll());
  }


  private getCardScrollAmount(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 400;
    }

    const container = this.trailerCardsContainer.nativeElement;
    const card = container.querySelector('.trailer-card-wrapper') as HTMLElement;

    if (!card) return 400;

    const gap = parseFloat(getComputedStyle(container).columnGap || '0');
    return card.offsetWidth + gap;
  }

  scrollLeft() {
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    const scrollAmount = this.getCardScrollAmount();

    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });

    this.updateScrollState();
  }

  scrollRight() {
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    const scrollAmount = this.getCardScrollAmount();

    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });

    this.updateScrollState();
  }


  checkScroll() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
  }


}
